import * as React from 'react';
import { NetworkStatus } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { SelectInput } from '@coverright/ui/inputs';
import { ArrowsClockwise, CaretLeft, CaretRight, Plus } from '@phosphor-icons/react';
import moment, { Moment } from 'moment';
import { enqueueSnackbar } from 'notistack';
import {
  CalendarEventOutput,
  CalendarEventUpdatesDocument,
  CalendarEventUpdatesSubscription,
  CalendarEventUpdatesSubscriptionVariables,
  useCalendarEventsQuery,
} from '../calendar-types';
import { WeekView } from './week-view';
import { MonthView } from './month-view';
import { DayView } from './day-view';
import { EventDetailDrawer } from './event-detail-drawer';
import { EventFormModal } from './event-form-modal';
import { isCalendarAuthError } from './calendar-utils';

type ViewMode = 'day' | 'week' | 'month';

interface CalendarViewProps {
  agentEmail?: string | null;
  ownEmail?: string | null;
  isOwnCalendar?: boolean;
}

export const CalendarView = ({ agentEmail, ownEmail, isOwnCalendar }: CalendarViewProps) => {
  const [viewMode, setViewMode] = React.useState<ViewMode>('week');
  const [currentDate, setCurrentDate] = React.useState<Moment>(moment().startOf('day'));
  const [selectedEvent, setSelectedEvent] = React.useState<CalendarEventOutput | null>(null);
  const [formOpen, setFormOpen] = React.useState(false);
  const [defaultSlot, setDefaultSlot] = React.useState<string | null>(null);
  const [events, setEvents] = React.useState<CalendarEventOutput[]>([]);
  const [authError, setAuthError] = React.useState(false);
  const mutatedEventIds = React.useRef<Set<string>>(new Set());

  const rangeStart = viewMode === 'day'
    ? currentDate.clone().startOf('day')
    : viewMode === 'week'
    ? currentDate.clone().startOf('week')
    : currentDate.clone().startOf('month').startOf('week');

  const rangeEnd = viewMode === 'day'
    ? currentDate.clone().endOf('day')
    : viewMode === 'week'
    ? currentDate.clone().endOf('week')
    : currentDate.clone().endOf('month').endOf('week');

  const { data, loading, error, refetch, networkStatus, subscribeToMore } = useCalendarEventsQuery({
    variables: {
      agentEmail,
      input: {
        timeMin: rangeStart.toISOString(),
        timeMax: rangeEnd.toISOString(),
        maxResults: 250,
      },
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: authError,
  });

  const isRefetching = networkStatus === NetworkStatus.refetch;

  React.useEffect(() => {
    if (error && isCalendarAuthError(error.message)) {
      setAuthError(true);
    }
  }, [error]);

  React.useEffect(() => {
    if (data?.calendarEvents?.events) {
      setEvents(data.calendarEvents.events);
    }
  }, [data?.calendarEvents?.events]);

  React.useEffect(() => {
    if (!selectedEvent) return;
    const updated = events.find((e) => e.id === selectedEvent.id);
    if (updated && updated !== selectedEvent) setSelectedEvent(updated);
  }, [events, selectedEvent]);

  React.useEffect(() => {
    if (authError) return;
    const onFocus = () => refetch();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refetch, authError]);

  React.useEffect(() => {
    const emailForSubscription = agentEmail ?? ownEmail;
    if (!emailForSubscription || authError) return;

    return subscribeToMore<CalendarEventUpdatesSubscription, CalendarEventUpdatesSubscriptionVariables>({
      document: CalendarEventUpdatesDocument,
      variables: { agentEmail: emailForSubscription },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data?.calendarEventUpdates) return prev;
        const { changeType, event, eventId } = subscriptionData.data.calendarEventUpdates;
        const current = prev.calendarEvents?.events ?? [];


        const fromInApp = eventId ? mutatedEventIds.current.has(eventId) : false;
        if (eventId) mutatedEventIds.current.delete(eventId);

        if (!fromInApp) {
          if ((changeType === 'CREATED' || changeType === 'UPDATED') && event) {
            const existing = current.find((e) => e.id === eventId);
            if (!existing) {
              enqueueSnackbar(`New event: "${event.summary || '(No title)'}"`, { variant: 'info' });
            } else if (JSON.stringify(existing) !== JSON.stringify(event)) {
              enqueueSnackbar(`Event updated: "${event.summary || '(No title)'}"`, { variant: 'info' });
            }
          } else if (changeType === 'DELETED' && current.some((e) => e.id === eventId)) {
            enqueueSnackbar('An event was removed from the calendar', { variant: 'info' });
          }
        }

        let updatedEvents: typeof current;
        switch (changeType) {
          case 'CREATED':
          case 'UPDATED': {
            if (!event) return prev;
            const exists = current.some((e) => e.id === eventId);
            updatedEvents = exists
              ? current.map((e) => (e.id === eventId ? event : e))
              : [...current, event];
            break;
          }
          case 'DELETED':
            updatedEvents = current.filter((e) => e.id !== eventId);
            break;
          default:
            return prev;
        }

        return {
          ...prev,
          calendarEvents: { ...prev.calendarEvents, events: updatedEvents },
        };
      },
    });
  }, [agentEmail, ownEmail, subscribeToMore]);

  const navigate = (direction: -1 | 1) => {
    setCurrentDate((prev) =>
      prev.clone().add(direction, viewMode === 'day' ? 'day' : viewMode === 'week' ? 'week' : 'month')
    );
  };

  const goToday = () => setCurrentDate(moment().startOf('day'));

  const handleEventClick = (event: CalendarEventOutput) => setSelectedEvent(event);
  const handleSlotClick = (datetime: string) => {
    setDefaultSlot(datetime);
    setFormOpen(true);
  };

  const weekStart = currentDate.clone().startOf('week');
  const monthStart = currentDate.clone().startOf('month');

  const title = viewMode === 'day'
    ? currentDate.format('MMMM D, YYYY')
    : viewMode === 'week'
    ? (() => {
        const start = weekStart;
        const end = weekStart.clone().endOf('week');
        return start.month() === end.month()
          ? `${start.format('MMMM D')} – ${end.format('D, YYYY')}`
          : `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
      })()
    : currentDate.format('MMMM YYYY');

  const containerSx = { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', borderRadius: 1, border: '1px solid', borderColor: 'divider' };

  if (authError) {
    return (
      <Box sx={containerSx}>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.paper' }}>
          <Alert severity="error" sx={{ maxWidth: 520 }}>
            {isOwnCalendar
              ? 'Could not load calendar. Make sure you have a CoverRight domain email and your Google account is connected to the workspace.'
              : 'Could not load calendar. Make sure the agent has a CoverRight domain email and their Google account is connected to the workspace.'}
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={containerSx}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        px={2}
        py={1}
        sx={{ borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
      >
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Button
            size="small"
            variant="outlined"
            onClick={goToday}
            sx={{ minWidth: 60 }}
          >
            Today
          </Button>
          <ButtonGroup size="small">
            <Button onClick={() => navigate(-1)} sx={{ px: 1, border: 'none', '&:hover': { border: 'none' } }}>
              <CaretLeft size={16} />
            </Button>
            <Button onClick={() => navigate(1)} sx={{ px: 1, border: 'none', '&:hover': { border: 'none' } }}>
              <CaretRight size={16} />
            </Button>
          </ButtonGroup>
          <Typography variant="subtitle1" fontWeight={500} fontSize={20}>
            {title}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" gap={1.5}>
          <LoadingButton
            loading={isRefetching}
            variant="outlined"
            size="small"
            startIcon={<ArrowsClockwise weight="bold" />}
            onClick={() => refetch()}
          >
            Refresh
          </LoadingButton>

          <SelectInput
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value as ViewMode)}
            hideTick
            className="mt-0 w-120"
          >
            <MenuItem value="day">Day</MenuItem>
            <MenuItem value="week">Week</MenuItem>
            <MenuItem value="month">Month</MenuItem>
          </SelectInput>

          <Button
            variant="contained"
            size="small"
            startIcon={<Plus />}
            sx={{ minWidth: 150, alignSelf: 'center' }}
            onClick={() => {
              setDefaultSlot(null);
              setFormOpen(true);
            }}
          >
            New event
          </Button>
        </Stack>
      </Stack>

      {error && !authError && (
        <Alert severity="error" sx={{ mx: 2, mt: 1, flexShrink: 0 }}>
          {error.message}
        </Alert>
      )}

      <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {loading && events.length === 0 && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.paper', zIndex: 5, opacity: 0.7 }}>
            <CircularProgress />
          </Box>
        )}

        {viewMode === 'day' && (
          <DayView day={currentDate} events={events} onEventClick={handleEventClick} onSlotClick={handleSlotClick} />
        )}
        {viewMode === 'week' && (
          <WeekView weekStart={weekStart} events={events} onEventClick={handleEventClick} onSlotClick={handleSlotClick} />
        )}
        {viewMode === 'month' && (
          <MonthView monthStart={monthStart} events={events} onEventClick={handleEventClick} onSlotClick={handleSlotClick} />
        )}
      </Box>

      <EventDetailDrawer
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        agentEmail={agentEmail}
        onMutationComplete={(eventId) => { if (eventId) mutatedEventIds.current.add(eventId); }}
      />
      <EventFormModal
        open={formOpen}
        onClose={() => { setFormOpen(false); setDefaultSlot(null); }}
        agentEmail={agentEmail}
        defaultStart={defaultSlot}
        onMutationComplete={(eventId) => { if (eventId) mutatedEventIds.current.add(eventId); }}
      />
    </Box>
  );
};
